import "dayjs/locale/fr";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DeviceType, deviceType } from "expo-device";
import TextField, { TextFieldProps } from "../TextField";
import { TimeFieldProps } from "./TimeField";
import BaseField from "../BaseField";
import { Pressable, StyleSheet } from "react-native";
import { MIDNIGHT_MINUTES, minutesAsDate, minutesAsTimeString } from "../../utils";
import { useFormatDateAsTimeString } from "./utils";
import { useTheme } from "../../hooks/use-theme";
import { useDropdown } from "../../hooks/use-dropdown";
import { Mask } from "react-native-mask-input";

const onTimeChangeText = (value: string, isDuration?: boolean) => {
    if (!isDuration && value.length === 1 && /[3-9]/.test(value)) {
        value = `0${value}:`;
    }
    return value;
};

// always true except for Desktop
export const hasNativeTimePicker = deviceType !== DeviceType.DESKTOP;

export const TimeField = ({
    value,
    disabled,
    onChange,
    isDuration = false,
    testID,
    style,
    label,
    placeholder,
    inputStyle,
    error,
    setTimeFn,
    popupFallback,
    onFocus,
    onBlur,
    ...props
}: TimeFieldProps) => {
    const formatDateAsTimeString = useFormatDateAsTimeString();
    const styles = useStyles();

    const inputRef = useRef<HTMLInputElement>(null);

    const [showTimePicker, setShowTimeChooser] = useState(false);
    const [time, setTime] = useState(
        value !== undefined
            ? value === MIDNIGHT_MINUTES
                ? "0:00"
                : minutesAsTimeString(value)
            : "",
    );

    useEffect(() => {
        if (showTimePicker) {
            inputRef.current?.showPicker();
            inputRef.current?.focus();
        }
    }, [showTimePicker]);

    useEffect(() => {
        const arr = time?.split(":");
        if (arr?.[1]?.length === 2) {
            const hours = Number.parseInt(arr[0]);
            const minutes = Number.parseInt(arr[1]);

            const totalMinutes = hours * 60 + minutes;
            // 0 and MIDNIGHT_MINUTES both represent midnight — treat as equivalent
            const effectiveValue = value === MIDNIGHT_MINUTES ? 0 : value;
            if (effectiveValue !== totalMinutes) {
                onChange?.(totalMinutes);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [time]);

    useEffect(() => {
        if (setTimeFn) {
            const fnName = `set${setTimeFn}`;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window[fnName] = setTime;

            return () => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window[fnName] = null;
            };
        }
    }, [setTimeFn]);

    const timeMask: Mask = useCallback(
        (text = "") => {
            const cleanTime = text.replace(/\D+/g, "");

            if (isDuration) {
                // Duration mode: accepts any duration (unlimited)
                const colonIndex = text.indexOf(":");
                const mask: (string | RegExp)[] = [];

                if (colonIndex === -1) {
                    // No colon yet: only accept digits
                    for (let i = 0; i < text.length; i++) {
                        mask.push(/\d/);
                    }
                } else {
                    // Colon present: digits before, then ":", then max 2 digits for minutes
                    for (let i = 0; i < colonIndex; i++) {
                        mask.push(/\d/);
                    }
                    mask.push(":");
                    mask.push(/[012345]/); // First digit of minutes
                    mask.push(/\d/); // Second digit of minutes
                }

                return mask;
            } else {
                // Time mode: limit to 23:59
                const hourFirstDigit = /\d/;
                const hourSecondDigit = cleanTime[0] === "2" ? /[0-3]/ : /\d/;

                const minuteFirstDigit = /[012345]/;
                const minuteSecondDigit = /\d/;

                return [
                    hourFirstDigit,
                    ...(text.charAt(1) !== ":" ? [hourSecondDigit] : []),
                    ":",
                    minuteFirstDigit,
                    minuteSecondDigit,
                ];
            }
        },
        [isDuration],
    );

    const baseFieldProps = useMemo(
        () => ({
            testID,
            style: [styles.baseField, style],
            inputStyle: [error ? styles.errorField : null, inputStyle],
            disabled,
            label,
            placeholder,
            error,
            isFocused: showTimePicker,
            selectTextOnFocus: true,
            mask: timeMask,
            ...props,
        }),
        [
            disabled,
            error,
            inputStyle,
            label,
            placeholder,
            props,
            showTimePicker,
            style,
            styles.baseField,
            styles.errorField,
            testID,
            timeMask,
        ],
    );

    const { onPress: onDropdownPress, ref: dropdownRef } = useDropdown({
        component: ({ fadeOut }) => (
            <PopupTimeField
                isDuration={isDuration}
                initialValue={value}
                onFinish={(newValue) => {
                    setTime(newValue);
                    fadeOut();
                }}
                {...baseFieldProps}
            />
        ),
        onBlur,
    });

    return hasNativeTimePicker ? (
        <BaseField
            component={
                <>
                    <input
                        type="time"
                        id={testID}
                        onChange={(e) => {
                            if (e.target.value) {
                                setTime(e.target.value);
                            }
                        }}
                        style={{
                            position: "absolute",
                            height: "100%",
                            minWidth: "100%",
                            opacity: 0,
                        }}
                        onBlur={() => {
                            setShowTimeChooser(false);
                        }}
                        value={time}
                        ref={inputRef}
                    />
                </>
            }
            value={
                value !== undefined
                    ? formatDateAsTimeString(minutesAsDate(value), isDuration)
                    : undefined
            }
            {...baseFieldProps}
        />
    ) : popupFallback ? (
        <Pressable
            ref={dropdownRef}
            id={testID}
            style={{
                position: "absolute",
                height: "100%",
                width: "100%",
                zIndex: 100,
            }}
            disabled={disabled}
            onPress={() => {
                onDropdownPress();
                onFocus?.();
            }}
        />
    ) : (
        <TextField
            onChangeText={(newTime) => {
                setTime(onTimeChangeText(newTime, isDuration));
            }}
            {...baseFieldProps}
            value={time}
        />
    );
};

type PopupTimeFieldProps = TextFieldProps & {
    initialValue?: number;
    onFinish: (value: string) => void;
    isDuration?: boolean;
};

const PopupTimeField = ({ onFinish, initialValue, isDuration, ...props }: PopupTimeFieldProps) => {
    const styles = useStyles();
    const [time, setTime] = useState(initialValue ? minutesAsTimeString(initialValue) : "");

    return (
        <TextField
            {...props}
            style={styles.textInput}
            selectTextOnFocus
            autoFocus
            onChangeText={(newTime) => {
                setTime(onTimeChangeText(newTime, isDuration));
            }}
            value={time}
            onSubmitEditing={() => {
                onFinish(time);
            }}
            onBlur={() => {
                onFinish(time);
            }}
        />
    );
};

const useStyles = () => {
    const { colors, roundness } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                textInput: {
                    borderWidth: 2,
                    borderColor: colors.primary,
                    backgroundColor: colors.background,
                    left: 0,
                    right: -8,
                    paddingVertical: 4,
                    paddingHorizontal: 8,
                    borderRadius: roundness,
                    zIndex: 5,
                    marginTop: -28,
                    marginRight: -8,
                    marginLeft: -8,
                    height: 38,
                    width: 64, // Adjust width 50 + 2 * margin
                    textAlign: "center",
                },
                baseField: {
                    maxWidth: 120,
                },
                errorField: {
                    color: colors.error,
                },
            }),
        [colors.background, colors.error, colors.primary, roundness],
    );
};
